import { useContext, useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom";
import { Card } from "@mui/material"
import { Api, ContactType} from "../apiclient/NewsletterAPI";
import ReCaptcha from "@matt-block/react-recaptcha-v2";
import { NewsletterSettingsContext } from "../context/NewsletterSettingsContext";
import { setWithExpiry, getWithExpiry } from "../utilities/useLocalStorage";
import { daysToMilliseconds } from "../utilities/daysToMilliseconds";
import { EmailVerified } from "./NewsletterWidgetComponents/EmailVerifiedComponent";
import { AlreadySubscribed } from "./NewsletterWidgetComponents/AlreadySubscribedComponent";
import { CaptchaVerified } from "./NewsletterWidgetComponents/CaptchaVerifiedComponent";
import { FormComponent } from "./NewsletterWidgetComponents/FormComponent";
import { Collapsed } from "./NewsletterWidgetComponents/CollapsedComponent";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import IconButton from "@mui/material/IconButton";

const NewsletterSignupWidget = () => {
    const [switcher, setSwitcher] = useState("form")
    const [minimized, setMinimized] = useState(true)
    const [input, setInput] = useState<string>("")
    const settings = useContext(NewsletterSettingsContext)
    const [urlState, setUrlState] = useSearchParams()
    const RecaptchaKey: string = "6LdLhaImAAAAAD2WT_nVFnCtLFJcXy5cF08iW1hs"
    var newsletter = new Api({ baseUrl: "https://api.newsletter.loyco.io" })

    /* Creates initial localstorage subscription state */
    if(!getWithExpiry("loyall.newsletter.state")){
        localStorage.setItem("loyall.newsletter.state", JSON.stringify({subscribed: false}))
    }

    /* Sets subscribed state to false if older than 30 days */
    getWithExpiry("loyall.newsletter.state")

    /* Checks if already subscribed */
    useMemo(() => {
        if(getWithExpiry("loyall.newsletter.state").subscribed) {
            if(urlState.get("subscriptionState") !== "new") {
                setSwitcher("already_subscribed")
            }
        }
    },[urlState])

    /* Reading subscription state from url */
    useMemo(() => {
        if (urlState.get("subscriptionState") === "new") {
            setSwitcher("email_verified")
        }
    }, [urlState])

    /* Scaling the recaptcha to fit the container using vanilla JS */
    useEffect(() => {
        function resizeReCaptcha() {
            const container = document.querySelector(".g-recaptcha")?.parentElement;
            if (!container) return;
            
            const width = container.clientWidth;
            const scale = width > 1 ? width / 304 : 1;
            
            const recaptcha = document.querySelector(".g-recaptcha") as HTMLElement;
            if (recaptcha) {
                recaptcha.style.transform = `scale(${scale})`;
                recaptcha.style.transformOrigin = 'center';
            }
        }
        resizeReCaptcha()
    })

    /* conditional rendering of component parts */
    return (
        switcher === "email_verified" ?
        <EmailVerified/>
        :
        settings.floating && minimized ? <Collapsed minimized={minimized} setMinimized={setMinimized}/> : 
        <Card className="newsletterBox" sx={{
            padding: "32px",
            display: "flex",
            position: "relative",
            justifyContent: "center",
            flexDirection:"column",
            maxWidth: "368px"/* "23rem" */,
            minWidth: "320px",
            minHeight: "266px"/* "16.6rem" */,
            ".g-recaptcha": {
                position: "absolute",
                alignSelf: "center"
            }
            
            }}>
            {switcher === "already_subscribed" &&
                <AlreadySubscribed setSwitcher={setSwitcher}/>
            }

            {switcher === "captcha_verified" &&
                <CaptchaVerified/>
            }

            {switcher === "captcha" &&
                <ReCaptcha siteKey={RecaptchaKey}
                    onSuccess={async (captcha) => {
                        await newsletter.api.subscriptionCreate(settings.newsletterID, {
                            data: {
                                contactType: ContactType.Email,
                                contactValue: input,
                                captchaResult: captcha
                            }
                        })
                        setWithExpiry("loyall.newsletter.state", true, daysToMilliseconds(30))
                        setSwitcher("captcha_verified")
                    }}
                    onExpire={() => console.log("Verification has expired, re-verify.")}
                    onError={() => console.log("Something went wrong, check your connection")}
                    size="normal"/>
            }

            {switcher === "form" && 
                <FormComponent input={input} setInput={setInput} setSwitcher={setSwitcher}/>
            }
            {settings.floating &&
                <IconButton onClick={() => setMinimized(!minimized)} sx={{position: "absolute", top: "0", right: "0"}}>
                    <ArrowDropDown/>
                </IconButton>
            }
            
        </Card>
    )
}

export default NewsletterSignupWidget
